import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { OrderListResponseDto } from "@/api/dtos";
import { OrderTags } from "./OrderTags";
import { toOrderListParams } from "./getOrderList";
import type { GetOrderListArgs } from "./getOrderList";
import type { Order } from "./_models/Order";
import { toOrderFromOrderDto } from "./_transformers/toOrderFromOrderDto";

export interface GetOrderPageArgs extends GetOrderListArgs {
  offset: number;
  /** Orders per page (the API allows 1–500). */
  limit: number;
}

export interface OrderPage {
  orders: Order[];
  /** Orders matching the filter across all pages. */
  total: number;
}

/** One page of orders (GET /orders ?offset= &limit=) and the filter's total; same filters as getOrderList. */
export const getOrderPageApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrderPage: builder.query<OrderPage, GetOrderPageArgs>({
      queryFn: async (args) => {
        try {
          const params = { ...toOrderListParams(args), offset: String(args.offset), limit: String(args.limit) };
          const res = await axios.get<OrderListResponseDto>(
            `${backendBase()}/orders`,
            { params, headers: { Accept: "application/json" } },
          );
          const orders = res.data.orders.map((x) => toOrderFromOrderDto(x));
          const page: OrderPage = { orders, total: res.data.total ?? args.offset + orders.length };
          return { data: page };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: [{ type: OrderTags.Order, id: "LIST" }],
    }),
  }),
});

export const getOrderPage = getOrderPageApi.endpoints.getOrderPage;
